import { z } from "zod";
import { PAY_REPORT_NOTE_MAX_LENGTH } from "@goppy/domain";
import { PAYMENT_CONFIRMATION_STATUS, PAYMENT_CONFIRMATION_TARGET, paymentConfirmationTargetSchema } from "./collections.js";
import { evidenceContentKindSchema, evidenceKeySchema } from "./contribution-evidence.js";
import { groupRoleSchema } from "./groups.js";
import { paymentConfirmationReadSchema } from "./payment-confirmation-read.js";
import { amountMinorString } from "./record-income.js";

/* ===========================================================================
 * 🔴 US-E6.F3-0913 (@UC0057 @UC0051 @UC9588 · QĐ người dùng 06/10 + 07/10/2026) — LỜI KHAI ĐÓNG GÓP "QUỸ CHUNG".
 *
 * Member khai một khoản đóng góp KHÔNG gắn kỳ thu, cho MỘT suất mình đại diện. Lời khai LUÔN dừng ở trạng thái (2) `submitted`
 * (§3.7 — không vế cờ, không vế vai); O/A/T duyệt ⇒ bút toán `SPONSORSHIP_MEMBER` qua CHÍNH lõi ghi thu của `/funds/:id/income`.
 * Không txType mới, không cột tiền mới — chỉ một ĐÍCH mới của `payment_confirmations` (CHECK `payment_confirmations_target_check`).
 * ========================================================================= */

/**
 * ĐÍCH của một lời khai — đã CHUYỂN sang `collections.ts` (cùng họ bảng với `PAYMENT_CONFIRMATION_STATUS`) khi
 * `groups.ts` thành người dùng thứ hai: để hằng ở đây thì file kia tạo vòng import (`general-fund-claim` → `groups`).
 * Re-export nguyên văn ⇒ mọi đường import cũ (`@goppy/contracts`, `./general-fund-claim.js`) không đổi một ký tự.
 */
export {
  PAYMENT_CONFIRMATION_TARGET,
  paymentConfirmationTargetSchema,
  type PaymentConfirmationTarget,
} from "./collections.js";

/** Tiền = STRING minor units (B1) — không float, không number trên wire. */
const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/**
 * POST /v1/funds/:id/contribution-claims — member khai đã chuyển một khoản đóng góp vào Quỹ chung.
 *
 *  - `groupMemberId` — suất ĐÃ CHỌN ở bước chọn member (G2). Server kiểm người gọi ĐẠI DIỆN suất đó; không thì 404 đồng nhất.
 *  - `amountMinor` — CÙNG trình kiểm số tiền của `/funds/:id/income` (số nguyên dương, ≤ BIGINT): khoản này khi duyệt đi đúng
 *    đường đó, nên biên của hai cửa phải là MỘT luật.
 *  - KHÔNG có `currency`: tiền tệ = tiền tệ QUỸ (ledger đơn-currency, ADR-0005). `.strict()` ⇒ gửi kèm `currency` là 400.
 *  - KHÔNG có `idempotencyKey` trong body: chống bấm kép bằng header `Idempotency-Key` (`@Idempotent()`); khoá bút toán lúc DUYỆT
 *    dẫn TẤT ĐỊNH từ id lời khai — server tự dựng.
 */
export const submitGeneralFundClaimRequestSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    amountMinor: amountMinorString,
    /** Ghi chú đối chiếu — CÙNG trần với lời khai kỳ thu (`PAY_REPORT_NOTE_MAX_LENGTH`, DB CHECK `payment_confirmations_note_len_check`). */
    note: z.string().trim().max(PAY_REPORT_NOTE_MAX_LENGTH).optional(),
    /** Khoá file bằng chứng (upload hai bước `POST /funds/:id/contribution-evidence`) — phải của CHÍNH người khai trong CHÍNH quỹ. */
    evidenceKey: evidenceKeySchema.optional(),
  })
  .strict();
export type SubmitGeneralFundClaimRequest = z.infer<typeof submitGeneralFundClaimRequestSchema>;

/** 201 — lời khai vừa tạo. `status` LUÔN `submitted` (G1): trạng thái "đã vào sổ" KHÔNG biểu diễn được ở response này. */
export const submitGeneralFundClaimResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    target: z.literal(PAYMENT_CONFIRMATION_TARGET.GENERAL_FUND),
    fundId: z.string().uuid(),
    groupMemberId: z.string().uuid(),
    claimedAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    status: z.literal(PAYMENT_CONFIRMATION_STATUS.SUBMITTED),
    note: z.string().nullable(),
    /** `image` | `pdf` khi có kèm file; null khi không. KHÔNG trả URL/khoá ở đây (đọc lại qua `GET /payment-confirmations/:id`). */
    proofContentKind: evidenceContentKindSchema.nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type SubmitGeneralFundClaimResponse = z.infer<typeof submitGeneralFundClaimResponseSchema>;

/**
 * 🔴 TRẦN số suất của `GET /v1/funds/:id/contribution-claims/seats` (QĐ người dùng 07/10/2026 — "Chỉ hiện suất của tôi").
 *
 * Cửa này KHÔNG phân trang (khác luật chung "mọi list có cursor"): nó liệt kê suất MỘT người đại diện trong MỘT nhóm — một phụ huynh
 * thật có nhiều nhất vài suất (số con trong một lớp). 50 ≈ 10× ca thật tệ nhất, cùng lý lẽ `CANCEL_JOIN_REQUEST_MAX_SEATS` (= 50,
 * `apps/api/src/modules/groups/groups.service.ts`) và tiền lệ mảng có trần `pendingMembers … .max(MEMBER_LIST_PENDING_PIN_LIMIT)` (`groups.ts`).
 * Server cắt ở trần (`take`, id tăng dần — tất định); suất thứ 51 trở đi vắng khỏi bước chọn người của mobile nhưng cửa khai VẪN nhận
 * nó (chốt quyền ở cửa khai không đổi) — mất UX ở một ca không người dùng thật nào chạm tới, không mất tiền.
 */
export const GENERAL_FUND_CLAIM_SEATS_MAX = 50;

/**
 * GET /v1/funds/:id/contribution-claims/seats — 200. Các suất NGƯỜI GỌI khai được cho Quỹ chung của quỹ này (QĐ người dùng 07/10/2026:
 * bước chọn người của form khai chỉ hiện suất mình ĐẠI DIỆN). CÙNG MỘT vị-từ với cửa khai `POST /funds/:id/contribution-claims` (server
 * `generalFundClaimSeatWhere`) ⇒ mọi id ở đây khai được; suất nào vắng ở đây thì cửa khai 404.
 *
 * CHỈ id, tăng dần (tất định). KHÔNG tên: mobile lọc danh sách thành viên đã tải (tên đã qua luật che tên của đường đó) — cửa này không
 * thành một đường đọc tên thứ hai. Rỗng = người gọi chưa đại diện suất nào trong nhóm (không phải lỗi).
 */
export const generalFundClaimSeatsResponseSchema = z
  .object({
    groupMemberIds: z.array(z.string().uuid()).max(GENERAL_FUND_CLAIM_SEATS_MAX),
  })
  .strict();
export type GeneralFundClaimSeatsResponse = z.infer<typeof generalFundClaimSeatsResponseSchema>;

/**
 * GET /v1/payment-confirmations/:id — đọc MỘT lời khai theo id (G6). Lời khai Quỹ chung không có `memberDueId` nên đường đọc theo
 * khoản (`GET /member-dues/:id/confirmations`) không với tới được.
 *
 * TÁI DÙNG khuôn `paymentConfirmationReadSchema` (§3.7: KHÔNG có field "tiền đã vào quỹ") — chỉ nới `memberDueId` thành nullable và
 * THÊM ba field đích. Envelope y hệt (`payerName` · `viewerRole`).
 */
export const paymentConfirmationByIdSchema = paymentConfirmationReadSchema
  .extend({
    /** `null` khi `target = general_fund`. */
    memberDueId: z.string().uuid().nullable(),
    target: paymentConfirmationTargetSchema,
    /** Quỹ của lời khai — cả hai đích (kỳ thu: quỹ của đợt). */
    fundId: z.string().uuid(),
    /** Suất được ghi tiền — cả hai đích (kỳ thu: chủ khoản; Quỹ chung: suất đã chọn lúc khai). */
    groupMemberId: z.string().uuid(),
  })
  .strict();
export type PaymentConfirmationById = z.infer<typeof paymentConfirmationByIdSchema>;

export const paymentConfirmationByIdResponseSchema = z
  .object({
    confirmation: paymentConfirmationByIdSchema,
    /** Tên suất được ghi tiền (`memberNameOf`); null khi suất chưa có tên lẫn người gắn — KHÔNG bịa. */
    payerName: z.string().nullable(),
    /** Vai người xem do SERVER nói (guard đã resolve) — dữ liệu hiển thị; chốt ghi vẫn ở `@RequireFundRoles` của approve/decline. */
    viewerRole: groupRoleSchema,
  })
  .strict();
export type PaymentConfirmationByIdResponse = z.infer<typeof paymentConfirmationByIdResponseSchema>;

/**
 * POST /v1/payment-confirmations/:id/approve — nhánh Quỹ chung (G3). Bút toán `SPONSORSHIP_MEMBER` (DEBIT CASH / CREDIT INCOME) qua
 * lõi ghi thu của `/income`. Không `newDueStatus` — không có khoản kỳ thu nào để đổi trạng thái.
 */
export const approveGeneralFundClaimResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    target: z.literal(PAYMENT_CONFIRMATION_TARGET.GENERAL_FUND),
    fundId: z.string().uuid(),
    groupMemberId: z.string().uuid(),
    ledgerTxId: z.string().uuid(),
    txType: z.literal("SPONSORSHIP_MEMBER"),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    newConfirmationStatus: z.literal(PAYMENT_CONFIRMATION_STATUS.APPROVED),
    approvedAt: z.string().datetime(),
  })
  .strict();
export type ApproveGeneralFundClaimResponse = z.infer<typeof approveGeneralFundClaimResponseSchema>;

/** POST /v1/payment-confirmations/:id/decline — nhánh Quỹ chung (G4). 0 bút toán. */
export const declineGeneralFundClaimResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    target: z.literal(PAYMENT_CONFIRMATION_TARGET.GENERAL_FUND),
    fundId: z.string().uuid(),
    groupMemberId: z.string().uuid(),
    newConfirmationStatus: z.literal(PAYMENT_CONFIRMATION_STATUS.REJECTED),
    reason: z.string(),
    declinedAt: z.string().datetime(),
  })
  .strict();
export type DeclineGeneralFundClaimResponse = z.infer<typeof declineGeneralFundClaimResponseSchema>;
